/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAccountState } from "../../redux/reducers/accountReducer";
import { TimeTrackingEntry, Task, UserAccount } from "../types";
import { useApi } from "../api/useApi";
import { useActiveTimeEntryService } from "./useActiveTimeEntryService";
import { useUtilities } from "../../utilities/useUtilities";
import { useGuid } from "../../utilities/useGuid";
import { useTimeTrackingState } from "../../redux/reducers/timeTrackingReducer";
import { useTimeTrackingActions } from "../../redux/actions/timeTrackingActions";

export const TimeTrackingApiRoutes = {
    getTimeTrackingEntry: (accountId: string, caseId: string, userId: string, entryId: string) => `account/${accountId}/case/${caseId}/user/${userId}/timeEntry/${entryId}`,
    getTimeEntriesForCase: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/timeEntries`,
    getTimeEntriesForUser: (accountId: string, userId: string, startDate?: Date, endDate?: Date) => `account/${accountId}/user/${userId}/timeEntries?startDate=${startDate?.toISOString()}&endDate=${endDate?.toISOString()}`,
    getTimeEntriesForAccount: (accountId: string, startDate?: Date, endDate?: Date) => `account/${accountId}/timeEntries?startDate=${startDate?.toISOString()}&endDate=${endDate?.toISOString()}`,
    getTimeEntryForTaskForToday: (accountId: string, taskId: string) => `account/${accountId}/task/${taskId}/today`,
    upsertTimeEntry: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/timeEntry`,
    startTimer: (accountId: string) => `account/${accountId}/timer/start`,
    stopTimer: (accountId: string) => `account/${accountId}/timer/stop`,
    deleteTimeEntry: (accountId: string, caseId: string, userId: string, entryId: string) => `account/${accountId}/case/${caseId}/user/${userId}/timeEntry/${entryId}`,
}

/** Both Task and TimeTrackingEntry structurally satisfy this, so either can be passed directly. */
export interface TimerObject {
    accountId?: string;
    caseId?: string;
    taskId?: string;
}

interface StartTimerRequest {
    accountId?: string;
    caseId?: string;
    taskId?: string;
    userId?: string;
    startTime?: Date;
}
interface StopTimerRequest {
    accountId?: string;
    caseId?: string;
    taskId?: string;
    userId?: string;
    endTime?: Date;
}
/** Starting a timer stops any other running timer, so the server reports both. */
export type StartTimerResult = {
    startedEntry?: TimeTrackingEntry;
    stoppedEntry?: TimeTrackingEntry;
}

export type TimeTrackingService = {
    timeEntries: TimeTrackingEntry[];
    billableTimeEntries: TimeTrackingEntry[];
    nonBillableTimeEntries: TimeTrackingEntry[];
    userTimeEntries: TimeTrackingEntry[];
    allEntriesLastWeek: TimeTrackingEntry[];
    allEntriesLastTwoWeeks: TimeTrackingEntry[];
    allEntriesLastMonth: TimeTrackingEntry[];
    getTimeTrackingEntry: (caseId: string, userId: string, entryId: string) => Promise<TimeTrackingEntry | undefined>;
    getTimeEntryForTaskForToday: (accountId: string, taskId: string) => Promise<TimeTrackingEntry | undefined>;
    getTimeEntriesForTaskAsync: (caseId: string, taskId: string) => Promise<TimeTrackingEntry[]>;
    upsertTimeEntry: (entry: TimeTrackingEntry) => Promise<void>;
    deleteTimeEntryAsync: (entry: TimeTrackingEntry) => Promise<void>;
    loadTimeEntriesForCase: (caseId: string) => Promise<void>;
    loadTimeEntriesForUser: (userId: string, startDate?: Date, endDate?: Date) => Promise<void>;
    loadTimeEntriesForAccount: (startDate?: Date, endDate?: Date) => Promise<void>;
    createTimeEntry: (caseId?: string, userId?: string, username?: string, taskId?: string, text?: string, hours?: number) => TimeTrackingEntry;
    groupEntriesByDate: (entries: TimeTrackingEntry[]) => Record<string, TimeTrackingEntry[]>;
    addTimeForTask: (task: Task, hours: number) => Promise<void>;
    startTimerAsync: (target?: TimerObject) => Promise<TimeTrackingEntry | undefined>;
    stopTimerAsync: (target?: TimerObject) => Promise<TimeTrackingEntry | undefined>;
    submitEntryAsync: (entry: TimeTrackingEntry) => Promise<void>;
    addOrMergeTimeEntryAsync: (entry: TimeTrackingEntry) => Promise<void>;
}
export enum TimeEntryFilterOptions {
    week = "Last 7 days",
    twoWeeks = "Last 14 days",
    month = "Last 30 days",
    all = "All",
}
/**
 * This service can be used to easily load time entries for an account or case.
 * It provides methods to interact with the time entries, such as starting/stopping timers, submitting entries, and deleting entries.
 * If caseId if provided, or changed, the service will reload based on the caseId provided
 * If userSpecific is true, and caseId is not provided, the service will load entries for the current user regardless of case.
 * @returns
 */
export const useTimeTrackingService = (): TimeTrackingService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account, user } = primaryAccount as UserAccount ?? {};
    const timeTrackingState = useTimeTrackingState();
    const timeTrackingActions = useTimeTrackingActions();
    const { setActiveTimeEntry, clearActiveTimeEntry, activeTimeEntry } = useActiveTimeEntryService();
    const utils = useUtilities();
    const guid = useGuid();

    //Api
    const api = useApi<TimeTrackingEntry>(`api/TimeTracking`);

    const getTimeTrackingEntry = async (caseId: string, userId: string, entryId: string) => {
        let entry = timeTrackingState.timeEntries.find((entry: TimeTrackingEntry) => entry.entryId === entryId);
        if (!entry) {
            entry = await api.getAsync(TimeTrackingApiRoutes.getTimeTrackingEntry(account.accountId as string, caseId, userId, entryId));
            if (entry) {
                timeTrackingActions.addOrReplaceTimeTrackingEntry(entry);
            }
        }
        return entry;
    }
    const loadTimeEntriesForCase = async (caseId: string) => {
        timeTrackingActions.setTimeTrackingEntries([]);
        const entries = await api.getAsync(TimeTrackingApiRoutes.getTimeEntriesForCase(account.accountId as string, caseId));
        timeTrackingActions.setTimeTrackingEntries(entries);
    }
    /** Fetches the case entries and narrows them to a single task without touching shared time tracking state. */
    const getTimeEntriesForTaskAsync = async (caseId: string, taskId: string): Promise<TimeTrackingEntry[]> => {
        if (!caseId || !taskId) return [];

        const entries: TimeTrackingEntry[] = await api.getAsync(TimeTrackingApiRoutes.getTimeEntriesForCase(account.accountId as string, caseId));
        return (entries || []).filter((entry: TimeTrackingEntry) => entry.taskId === taskId);
    }
    const loadTimeEntriesForUser = async (userId: string, startDate?: Date, endDate?: Date) => {
        timeTrackingActions.setTimeTrackingEntries([]);
        const entries = await api.getAsync(TimeTrackingApiRoutes.getTimeEntriesForUser(account.accountId as string, userId, startDate, endDate));
        timeTrackingActions.setTimeTrackingEntries(entries);
    }
    const loadTimeEntriesForAccount = async (startDate?: Date, endDate?: Date) => {
        timeTrackingActions.setTimeTrackingEntries([]);
        const entries = await api.getAsync(TimeTrackingApiRoutes.getTimeEntriesForAccount(account.accountId as string, startDate, endDate));
        timeTrackingActions.setTimeTrackingEntries(entries);
    };
    const getTimeEntryForTaskForToday = async (taskId: string) => {
        let entry = timeTrackingState.timeEntries.find((entry: TimeTrackingEntry) => entry.taskId === taskId && utils.date.isToday(new Date(entry.date as Date)));
        if (!entry) {
            entry = await api.getAsync(TimeTrackingApiRoutes.getTimeEntryForTaskForToday(account.accountId as string, taskId));
            if (entry) {
                timeTrackingActions.addOrReplaceTimeTrackingEntry(entry);
            }
        }
        return entry;
    }
    const upsertTimeEntry = async (entry: TimeTrackingEntry) => {
        timeTrackingActions.addOrReplaceTimeTrackingEntry(entry);
        const updatedEntry = await api.postAsync(entry, TimeTrackingApiRoutes.upsertTimeEntry(account.accountId as string, entry.caseId as string));
        timeTrackingActions.addOrReplaceTimeTrackingEntry(updatedEntry);
    }
    const deleteTimeEntryAsync = async (entry: TimeTrackingEntry) => {
        await api.deleteAsync([entry.entryId as string], TimeTrackingApiRoutes.deleteTimeEntry(account.accountId as string, entry.caseId as string, entry.userId as string, entry.entryId as string));
        timeTrackingActions.removeTimeTrackingEntry(entry.entryId as string);
    }


    //Service Functions
    const filterLastWeek = (entry: TimeTrackingEntry) => {
        const today = new Date();
        const entryDate = new Date(entry.date!);

        const oneWeek = new Date();
        oneWeek.setDate(today.getDate() - 7);

        return entryDate >= oneWeek;
    }
    const filterTwoWeek = (entry: TimeTrackingEntry) => {
        const today = new Date();
        const entryDate = new Date(entry.date!);

        const twoWeek = new Date();
        twoWeek.setDate(today.getDate() - 14);

        return entryDate >= twoWeek;
    }
    const filterLastMonth = (entry: TimeTrackingEntry) => {
        const today = new Date();
        const entryDate = new Date(entry.date!);

        const month = new Date();
        month.setDate(today.getDate() - 31);

        return entryDate >= month;
    }
    const groupEntriesByDate = (entries: TimeTrackingEntry[]) => {

        const sortedEntries = entries.sort(sortEntries);

        return sortedEntries.reduce((groups: Record<string, TimeTrackingEntry[]>, entry) => {
            const dateKey = new Date(entry.date!).toLocaleDateString(); // Format as needed
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(entry);
            return groups;
        }, {});
    }

    const createTimeEntry = (caseId?: string, userId?: string, username?: string, taskId?: string, text?: string, hours?: number) => {
        const newEntry: TimeTrackingEntry = {
            accountId: account.accountId,
            caseId: caseId || '',
            userId: userId || '',
            username: username || '',
            entryId: guid.newGuid(),
            taskId: taskId || '',
            date: new Date(),
            description: text || '',
            hours: hours || 0
        };
        return newEntry;
    }

    const createTimeEntryFromTask = (task: Task) => {
        const newEntry: TimeTrackingEntry = {
            accountId: task.accountId,
            caseId: task.caseId,
            userId: user.userId,
            username: user.username,
            entryId: guid.newGuid(),
            taskId: task.taskId,
            date: new Date(),
            description: task.title || task.text || '',
            hours: 0,
            isBillable: task.billable
        };
        return newEntry;
    }

    const addTimeForTask = async (task: Task, hours: number) => {
        const existinEntry = timeTrackingState.timeEntries.find((d) => d.entryId == task.taskId);
        if (existinEntry) {
            existinEntry.hours += hours;
            await upsertTimeEntry(existinEntry);
        } else {
            const newEntry = createTimeEntryFromTask(task);
            await upsertTimeEntry({ ...newEntry, hours });
        }
    }
    /**
     * Starts the timer for a task. The server finds or creates the task's entry for
     * today, stops any other running timer, and returns both entries.
     */
    const startTimerAsync = async (target?: TimerObject) => {
        if (!target?.taskId) return undefined;

        const request: StartTimerRequest = {
            accountId: target.accountId ?? account.accountId,
            caseId: target.caseId,
            taskId: target.taskId,
            userId: user.userId,
            startTime: new Date(),
        };

        const result: StartTimerResult = await api.putAsync(request, TimeTrackingApiRoutes.startTimer(account.accountId as string));
        
        const startedEntry = result?.startedEntry;
        if (!startedEntry) return undefined;

        if (!!result.stoppedEntry) {
            timeTrackingActions.addOrReplaceTimeTrackingEntry(result.stoppedEntry);
        }
        timeTrackingActions.addOrReplaceTimeTrackingEntry(startedEntry);
        setActiveTimeEntry(startedEntry);

        return startedEntry;
    }
    /** Stops the running timer. Without a target, stops whichever timer is currently active. */
    const stopTimerAsync = async (target?: TimerObject) => {
        if (!target?.taskId) return undefined;

        const runningEntry = activeTimeEntry?.taskId === target.taskId ? activeTimeEntry : undefined;
        const endTime = new Date();

        const request: StopTimerRequest = {
            accountId: target?.accountId ?? account.accountId,
            caseId: target?.caseId ?? '',
            taskId: target.taskId,
            userId: user.userId,
            endTime,
        };

        const stoppedEntry: TimeTrackingEntry = await api.putAsync(request, TimeTrackingApiRoutes.stopTimer(account.accountId as string));

        // The server may not echo the entry back, so accumulate locally to keep totals accurate.
        const updatedEntry = stoppedEntry ?? (!!runningEntry?.startTime
            ? {
                ...runningEntry,
                timerOn: false,
                startTime: undefined,
                hours: runningEntry.hours + Math.max(0, endTime.getTime() - new Date(runningEntry.startTime).getTime()) / 3600000,
            }
            : undefined);

        if (!!updatedEntry) {
            timeTrackingActions.addOrReplaceTimeTrackingEntry(updatedEntry);
        }
        clearActiveTimeEntry();

        return updatedEntry;
    }
    const addOrMergeTimeEntryAsync = async (entry: TimeTrackingEntry) => {
        const entryDate = entry.date ? new Date(entry.date) : new Date();

        let existingEntry = timeTrackingState.timeEntries.find((d: TimeTrackingEntry) =>
            d.taskId === entry.taskId
            && d.entryId !== entry.entryId
            && !!d.date
            && utils.date.isSameDay(new Date(d.date as Date), entryDate));

        // Only the current day can be looked up server-side; other dates rely on loaded state.
        if (!existingEntry && utils.date.isToday(entryDate)) {
            existingEntry = await getTimeEntryForTaskForToday(entry.taskId as string);
        }

        if (!!existingEntry) {
            const modifiedEntry: TimeTrackingEntry = { ...existingEntry, hours: Number(existingEntry.hours) + Number(entry.hours) };
            await upsertTimeEntry(modifiedEntry);
        } else {
            await upsertTimeEntry(entry);
        }
    }
    const sortEntries = (a: TimeTrackingEntry, b: TimeTrackingEntry) => {
        const aDate = !!a.date ? new Date(a.date).getTime() : 0;
        const bDate = !!b.date ? new Date(b.date).getTime() : 0;
        return aDate - bDate
    }


    return {
        timeEntries: timeTrackingState.timeEntries || [],
        billableTimeEntries: (timeTrackingState.timeEntries || []).filter((entry: TimeTrackingEntry) => entry.isBillable),
        nonBillableTimeEntries: (timeTrackingState.timeEntries || []).filter((entry: TimeTrackingEntry) => !entry.isBillable),
        userTimeEntries: (timeTrackingState.timeEntries || []).filter((entry: TimeTrackingEntry) => entry.userId === user.userId),
        allEntriesLastWeek: (timeTrackingState.timeEntries || []).filter(filterLastWeek),
        allEntriesLastTwoWeeks: (timeTrackingState.timeEntries || []).filter(filterTwoWeek),
        allEntriesLastMonth: (timeTrackingState.timeEntries || []).filter(filterLastMonth),
        getTimeTrackingEntry,
        upsertTimeEntry,
        deleteTimeEntryAsync,
        loadTimeEntriesForCase,
        loadTimeEntriesForUser,
        loadTimeEntriesForAccount,
        getTimeEntryForTaskForToday,
        getTimeEntriesForTaskAsync,
        createTimeEntry,
        groupEntriesByDate,
        addTimeForTask,
        startTimerAsync,
        stopTimerAsync,
        submitEntryAsync: upsertTimeEntry,
        addOrMergeTimeEntryAsync,
    };
}